import {
  type IntegrationsCapabilityModeEnum,
  IntegrationsSyncableStateStatusEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  type SyncStatusColumn,
  type SyncStatusColumnItem,
  type SyncStatusColumnValue,
  usePrivateConditionalIntegrationsSyncContext,
} from 'modules/integrations/hooks/SyncContext'
import { groupedStatuses } from 'modules/integrations/lib/syncableStatus'

export function useSyncColumn(disabled = false): SyncStatusColumn[] {
  const context = usePrivateConditionalIntegrationsSyncContext(disabled)
  const syncEnabled = context?.syncEnabled ?? false

  const FILE_MODE: IntegrationsCapabilityModeEnum = 'file'
  const isExportMode = context?.capabilityMode === FILE_MODE

  const syncableStateStatus = (
    syncable: SyncStatusColumnItem
  ): SyncStatusColumnValue['value']['status'] | undefined => {
    if (!syncEnabled || !syncable) return undefined

    const statuses: Set<IntegrationsSyncableStateStatusEnum> = new Set()

    if (syncable.__typename === 'ExpensesExpensable') {
      const status = syncable.syncableState?.[0]?.status
      if (status) statuses.add(status)
    } else if (syncable.__typename === 'CompensationsPayrollRunEmployee') {
      syncable.compensations
        .filter((compensation) => compensation.resultType === 'compensation')
        .flatMap((compensation) => compensation.syncableState?.map((s) => s.status) ?? [])
        .forEach((status) => {
          if (status) statuses.add(status)
        })
    }

    if (statuses.size === 0) return 'pending'

    return groupedStatuses([statuses])[0]
  }

  if (!syncEnabled) return []

  const exportLabel = i18n.t('integrations.columns.export_status_title')
  const syncLabel = i18n.t('integrations.columns.sync_status_title')

  const exportStatusTooltipTexts: Record<SyncStatusColumnValue['value']['status'], string> = {
    failed: i18n.t('integrations.columns.export_status.failed'),
    outdated: i18n.t('integrations.columns.export_status.outdated'),
    partiallySynced: i18n.t('integrations.columns.export_status.partially_synced'),
    pending: i18n.t('integrations.columns.export_status.pending'),
    synced: i18n.t('integrations.columns.export_status.synced'),
    syncing: i18n.t('integrations.columns.export_status.syncing'),
  }

  const syncStatusTooltipTexts: Record<SyncStatusColumnValue['value']['status'], string> = {
    failed: i18n.t('integrations.columns.sync_status.failed'),
    outdated: i18n.t('integrations.columns.sync_status.outdated'),
    partiallySynced: i18n.t('integrations.columns.sync_status.partially_synced'),
    pending: i18n.t('integrations.columns.sync_status.pending'),
    synced: i18n.t('integrations.columns.sync_status.synced'),
    syncing: i18n.t('integrations.columns.sync_status.syncing'),
  }

  const tooltipTexts = isExportMode ? exportStatusTooltipTexts : syncStatusTooltipTexts

  return [
    {
      label: isExportMode ? exportLabel : syncLabel,
      render: (item: SyncStatusColumnItem) => {
        const status = syncableStateStatus(item)
        if (status === undefined) return undefined

        return {
          type: 'syncStatus' as const,
          value: {
            status,
            tooltip: tooltipTexts[status],
          },
        }
      },
    },
  ]
}
